import React, { type FC, useEffect } from "react";
import { type GetStaticPaths, type GetStaticProps } from "next";
import { useRouter } from "next/router";
import {
  type Banner,
  type Integration,
  type JointIntegration,
  type Customer,
  type IntegrationSimple,
  type DestinationTiles as SanityDestinationTiles,
  getStackedLogos,
  getSimpleIntegrations,
  getJointIntegration,
  getDestinationTiles,
  getIntegration,
  getAnnouncementBanner,
} from "lib/sanity";
import { trackEvent } from "lib/analytics";
import { Box } from "@chakra-ui/react";
import { Page } from "components/page";
import { HalfHero } from "components/half-hero";
import { StackedLogos } from "components/logos";
import ActivateDataSteps from "components/activate-data-steps";
import { DestinationTiles } from "components/integrations/connection/destination-tiles";
import PaginatedLinks from "components/integrations/connection/paginated-links";
import { QueryTypes } from "components/integrations/connection/query-types";
import { Faq } from "components/integrations/connection/faq";
import { HeroImage } from "components/integrations/connection/hero-image";
import { DemoEmbedHalf } from "components/demo-embed";
import NotFound from "pages/404";
import { Checklist, ChecklistItem } from "ui/checklist";

type ConnectionProps = {
  source?: Integration;
  destination?: Integration;
  jointIntegration?: JointIntegration;
  customerRows: Customer[][];
  sources: IntegrationSimple[];
  destinations: IntegrationSimple[];
  destinationTiles?: SanityDestinationTiles;
  announcementBanner?: Banner;
};

const Connection: FC<Readonly<ConnectionProps>> = ({
  source,
  destination,
  jointIntegration,
  customerRows,
  sources,
  destinations,
  destinationTiles,
  announcementBanner,
}) => {
  const router = useRouter();

  useEffect(() => {
    if (source && destination) {
      trackEvent("Integration Page Viewed", {
        source_name: source?.name,

        destination_name: destination?.name,

        destination_status: destination?.status,

        joint_integration: true,
      });
    }
  }, [source, destination]);

  if (!router.isFallback && (!source || !destination)) {
    return <NotFound />;
  }

  if (!source || !destination) {
    return null;
  }

  let eyebrow = null;
  if (source.status === "alpha" || destination.status === "alpha") {
    eyebrow = "Private preview";
  } else if (source.status === "coming_soon" || destination.status === "coming_soon") {
    eyebrow = "Coming soon";
  }

  return (
    <Page
      canonicalUrl={`https://hightouch.com/integrations/${source.slug}-to-${destination.slug}`}
      announcementBanner={announcementBanner}
      title={jointIntegration?.metaTitle ?? `Sync data from ${source.name} to ${destination.name}`}
      description={
        jointIntegration?.metaDescription ??
        `Connect your data from ${source.name} to ${destination.name} with Hightouch. No APIs, no months-long implementations, and no CSV files. Just your data synced forever.`
      }
      footerOptions={{ variant: "blue" }}
    >
      <HalfHero
        eyebrow={eyebrow ?? undefined}
        title={`Sync data from ${source.name} to ${destination.name}`}
        description={`Connect your data from ${source.name} to ${destination.name} with Hightouch. No APIs, no months-long implementations, and no CSV files. Just your data synced forever.`}
        customImage={
          <Box flex={0.8}>
            <HeroImage width="100%" sourceImage={source.iconUrl} destinationImage={destination.iconUrl} />
          </Box>
        }
      />

      <StackedLogos customerRows={customerRows} />

      <ActivateDataSteps source={source} destination={destination} />

      <DemoEmbedHalf
        imageProps={{ width: "1400px", maxH: "700px" }}
        eyebrow="Use cases"
        title={`Sync data from ${source.name} to ${destination.name}`}
        description={
          destination.features && destination.features.length > 0 ? (
            <Checklist fontSize="md">
              {destination.features.map((feature) => (
                <ChecklistItem key={feature}>{feature}</ChecklistItem>
              ))}
            </Checklist>
          ) : (
            "Hightouch syncs audiences from the data warehouse to all the tools your teams rely on. Just define an audience and start syncing. No CSVs required."
          )
        }
      />

      {destinationTiles?.tiles && destinationTiles?.tiles.length > 0 && (
        <DestinationTiles source={source} destination={destination} tiles={destinationTiles.tiles} />
      )}

      {/* Only show query types if there is more than 1 */}
      {source.supportedQueryTypes && source.supportedQueryTypes.length > 1 && <QueryTypes source={source} />}

      <Faq source={source} destination={destination} jointIntegration={jointIntegration} />

      <PaginatedLinks
        my={0}
        source={source}
        destination={destination}
        allSources={sources
          .filter((currentSource) => currentSource.slug !== source.slug)
          .sort((a, b) => a.name.localeCompare(b.name))}
        allDestinations={destinations
          .filter((currentDestination) => currentDestination.slug !== destination.slug)
          .sort((a, b) => a.name.localeCompare(b.name))}
      />
    </Page>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<ConnectionProps> = async ({ params }) => {
  const sourceSlug = params?.slugs?.[0] ?? null;
  const destinationSlug = params?.slugs?.[1] ?? null;

  if (!sourceSlug || !destinationSlug) {
    return {
      notFound: true,
    };
  }

  const jointSlug = sourceSlug + "-to-" + destinationSlug;
  const [sources, destinations, jointIntegration, customerRows, destinationTiles, announcementBanner] =
    await Promise.all([
      getSimpleIntegrations("source"),
      getSimpleIntegrations("destination"),
      getJointIntegration(jointSlug),
      getStackedLogos(),
      getDestinationTiles(destinationSlug),
      getAnnouncementBanner(),
    ]);

  // Get data from X to Y (jointIntegration) object if one exists
  if (jointIntegration?.source && jointIntegration?.destination) {
    const source = jointIntegration.source;
    const destination = jointIntegration.destination;

    return {
      props: {
        jointIntegration,
        source,
        destination,
        sources,
        destinations,
        customerRows,
        destinationTiles,
        announcementBanner,
      },
      revalidate: 60,
    };
  }

  // Otherwise directly get source / destination data from Integration object
  const [source, destination] = await Promise.all([
    getIntegration(sourceSlug, "source"),
    getIntegration(destinationSlug, "destination"),
  ]);

  return {
    props: {
      jointIntegration,
      source,
      destination,
      sources,
      destinations,
      customerRows,
      destinationTiles,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export default Connection;
